import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NavLink, useParams } from 'react-router-dom';
import {
  Building2,
  FolderKanban,
  Layers,
  FileText,
  ChevronRight,
  ChevronDown,
  GripVertical,
  Loader2,
  Plus,
  X
} from 'lucide-react';
import { api } from '../lib/api';
import clsx from 'clsx';

// Types
interface Organization {
  id: string;
  name: string;
  slug: string;
}

interface Project {
  id: string;
  orgId: string;
  name: string;
  key: string;
  status: string;
}

interface Epic {
  id: string;
  projectId: string;
  title: string;
  status: string;
  isSystem: boolean;
  sortOrder: number;
}

interface Story {
  id: string;
  epicId: string | null;
  title: string;
  status: string;
}

// Expandable Tree Node
function TreeNode({
  icon: Icon,
  label,
  href,
  isActive,
  isExpanded,
  onToggle,
  level = 0,
  children,
  count,
  isDraggable,
  onDragStart,
  onDragOver,
  onDrop,
  isDropTarget,
  className
}: {
  icon: React.ElementType;
  label: string;
  href?: string;
  isActive?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  level?: number;
  children?: React.ReactNode;
  count?: number;
  isDraggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  isDropTarget?: boolean;
  className?: string;
}) {
  const hasChildren = !!children;
  const paddingLeft = 12 + level * 16;

  const content = (
    <div
      className={clsx(
        'group flex items-center gap-1.5 py-1.5 pr-2 text-sm rounded-md transition-all cursor-pointer select-none',
        isActive
          ? 'bg-accent-50 dark:bg-accent-900/30 text-accent-700 dark:text-accent-400 font-medium'
          : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 hover:text-neutral-900 dark:hover:text-neutral-100',
        isDropTarget && 'ring-2 ring-accent-400 bg-accent-50 dark:bg-accent-900/30',
        className
      )}
      style={{ paddingLeft }}
      draggable={isDraggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* Drag handle */}
      {isDraggable && (
        <GripVertical className="w-3 h-3 text-neutral-300 dark:text-neutral-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab flex-shrink-0" />
      )}
      
      {/* Expand/collapse button */}
      {hasChildren ? (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggle?.();
          }}
          className="p-0.5 rounded hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400" />
          )}
        </button>
      ) : (
        <span className="w-4" /> // Spacer
      )}

      {/* Icon */}
      <Icon className={clsx(
        'w-4 h-4 flex-shrink-0',
        isActive ? 'text-accent-600 dark:text-accent-400' : 'text-neutral-500 dark:text-neutral-400'
      )} />

      {/* Label */}
      <span className="flex-1 truncate">{label}</span>

      {/* Count badge */}
      {count !== undefined && count > 0 && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-200 dark:bg-neutral-600 text-neutral-600 dark:text-neutral-300 font-medium">
          {count}
        </span>
      )}
    </div>
  );

  return (
    <div>
      {href ? (
        <NavLink to={href} className="block">
          {() => (
            <div onClick={() => hasChildren && onToggle?.()}>
              {content}
            </div>
          )}
        </NavLink>
      ) : (
        <div onClick={() => hasChildren && onToggle?.()}>
          {content}
        </div>
      )}
      
      {hasChildren && isExpanded && (
        <div className="mt-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

// Story Item with Drag support
function StoryNode({
  story,
  orgId,
  projectId,
  epicId,
  level,
  onDragStart,
}: {
  story: Story;
  orgId: string;
  projectId: string;
  epicId: string;
  level: number;
  onDragStart: (storyId: string, fromEpicId: string) => void;
}) {
  const { storyId: activeStoryId } = useParams();
  
  return (
    <TreeNode
      icon={FileText}
      label={story.title}
      href={`/app/organizations/${orgId}/projects/${projectId}/epics/${epicId}/stories/${story.id}`}
      isActive={activeStoryId === story.id}
      level={level}
      isDraggable
      onDragStart={(e) => {
        e.dataTransfer.setData('storyId', story.id);
        e.dataTransfer.setData('fromEpicId', epicId);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(story.id, epicId);
      }}
    />
  );
}

// Epic Node with drop target
function EpicNode({
  epic,
  orgId,
  projectId,
  stories,
  level,
  expandedEpics,
  toggleEpic,
  onStoryDragStart,
  onStoryDrop,
  draggingStoryId,
}: {
  epic: Epic;
  orgId: string;
  projectId: string;
  stories: Story[];
  level: number;
  expandedEpics: Set<string>;
  toggleEpic: (epicId: string) => void;
  onStoryDragStart: (storyId: string, fromEpicId: string) => void;
  onStoryDrop: (storyId: string, fromEpicId: string, toEpicId: string) => void;
  draggingStoryId: string | null;
}) {
  const { epicId: activeEpicId } = useParams();
  const [isDropTarget, setIsDropTarget] = useState(false);
  
  const epicStories = stories.filter(s => s.epicId === epic.id);
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDropTarget(true);
  };

  const handleDragLeave = () => {
    setIsDropTarget(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDropTarget(false);
    
    const storyId = e.dataTransfer.getData('storyId');
    const fromEpicId = e.dataTransfer.getData('fromEpicId');
    
    if (storyId && fromEpicId && fromEpicId !== epic.id) {
      onStoryDrop(storyId, fromEpicId, epic.id);
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <TreeNode
        icon={Layers}
        label={epic.title}
        href={`/app/organizations/${orgId}/projects/${projectId}/epics/${epic.id}`}
        isActive={activeEpicId === epic.id}
        isExpanded={expandedEpics.has(epic.id)}
        onToggle={() => toggleEpic(epic.id)}
        level={level}
        count={epicStories.length}
        isDropTarget={isDropTarget && draggingStoryId !== null}
      >
        {epicStories.map(story => (
          <StoryNode
            key={story.id}
            story={story}
            orgId={orgId}
            projectId={projectId}
            epicId={epic.id}
            level={level + 1}
            onDragStart={onStoryDragStart}
          />
        ))}
      </TreeNode>
    </div>
  );
}

// Inline Epic Creator
function InlineEpicCreator({
  projectId,
  level,
  onClose,
  onSuccess,
}: {
  projectId: string;
  level: number;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  
  const createMutation = useMutation({
    mutationFn: async () => {
      return api.post('/epics', { projectId, title });
    },
    onSuccess: () => {
      onSuccess();
      onClose();
    },
  });
  
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  
  const handleSubmit = () => {
    if (title.trim()) {
      createMutation.mutate();
    }
  };
  
  const paddingLeft = 12 + (level + 1) * 16;
  
  return (
    <div 
      className="flex items-center gap-1.5 py-1.5 pr-2"
      style={{ paddingLeft }}
    >
      <Layers className="w-4 h-4 text-accent-500 flex-shrink-0" />
      <input
        ref={inputRef}
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit();
          if (e.key === 'Escape') onClose();
        }}
        placeholder="Epic name..."
        className="flex-1 bg-transparent border-none outline-none text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400"
      />
      <button
        onClick={handleSubmit}
        disabled={!title.trim() || createMutation.isPending}
        className="p-1 rounded hover:bg-accent-100 dark:hover:bg-accent-900/30 text-accent-600 dark:text-accent-400 disabled:opacity-50"
      >
        {createMutation.isPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Plus className="w-3.5 h-3.5" />
        )}
      </button>
      <button
        onClick={onClose}
        className="p-1 rounded hover:bg-neutral-200 dark:hover:bg-neutral-600 text-neutral-500"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// Project Node
function ProjectNode({
  project,
  orgId,
  level,
  expandedProjects,
  expandedEpics,
  toggleProject,
  toggleEpic,
  onStoryDragStart,
  onStoryDrop,
  draggingStoryId,
}: {
  project: Project;
  orgId: string;
  level: number;
  expandedProjects: Set<string>;
  expandedEpics: Set<string>;
  toggleProject: (projectId: string) => void;
  toggleEpic: (epicId: string) => void;
  onStoryDragStart: (storyId: string, fromEpicId: string) => void;
  onStoryDrop: (storyId: string, fromEpicId: string, toEpicId: string) => void;
  draggingStoryId: string | null;
}) {
  const { projectId: activeProjectId } = useParams();
  const queryClient = useQueryClient();
  const isExpanded = expandedProjects.has(project.id);
  const [showNewEpic, setShowNewEpic] = useState(false);
  
  // Fetch epics when expanded
  const { data: epicsData } = useQuery({
    queryKey: ['tree-epics', project.id],
    queryFn: () => api.get<{ data: Epic[] }>(`/epics?projectId=${project.id}`),
    enabled: isExpanded,
  });
  
  // Fetch stories from unified KB documents
  const { data: storiesData } = useQuery({
    queryKey: ['tree-stories', project.id],
    queryFn: async () => {
      const res = await api.get<{ data: any[] }>(`/kb/documents?project_id=${project.id}&type=user_story,requirement&limit=200`);
      return {
        data: (res.data || []).map((d: any): Story => ({
          id: d.id,
          epicId: d.epic_id || null,
          title: d.title,
          status: d.workflow_status || d.status || 'DRAFT',
        })),
      };
    },
    enabled: isExpanded,
  });

  const epics = epicsData?.data || [];
  const stories = storiesData?.data || [];
  
  const handleCreateEpicClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isExpanded) {
      toggleProject(project.id);
    }
    setShowNewEpic(true);
  };

  return (
    <div className="group/project">
      <div className="relative">
        <TreeNode
          icon={FolderKanban}
          label={project.name}
          href={`/app/organizations/${orgId}/projects/${project.id}`}
          isActive={activeProjectId === project.id}
          isExpanded={isExpanded}
          onToggle={() => toggleProject(project.id)}
          level={level}
          count={epics.length}
        >
          {epics.map(epic => (
            <EpicNode
              key={epic.id}
              epic={epic}
              orgId={orgId}
              projectId={project.id}
              stories={stories}
              level={level + 1}
              expandedEpics={expandedEpics}
              toggleEpic={toggleEpic}
              onStoryDragStart={onStoryDragStart}
              onStoryDrop={onStoryDrop}
              draggingStoryId={draggingStoryId}
            />
          ))}
          
          {/* Inline Epic Creator */}
          {showNewEpic && (
            <InlineEpicCreator
              projectId={project.id}
              level={level}
              onClose={() => setShowNewEpic(false)}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ['tree-epics', project.id] });
              }}
            />
          )}
        </TreeNode>
        
        {/* Add Epic button - appears on hover */}
        <button
          onClick={handleCreateEpicClick}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-accent-100 dark:hover:bg-accent-900/30 text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 opacity-0 group-hover/project:opacity-100 transition-opacity"
          title="Create Epic"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Organization Node
function OrgNode({
  org,
  level,
  expandedOrgs,
  expandedProjects,
  expandedEpics,
  toggleOrg,
  toggleProject,
  toggleEpic,
  onStoryDragStart,
  onStoryDrop,
  draggingStoryId,
}: {
  org: Organization;
  level: number;
  expandedOrgs: Set<string>;
  expandedProjects: Set<string>;
  expandedEpics: Set<string>;
  toggleOrg: (orgId: string) => void;
  toggleProject: (projectId: string) => void;
  toggleEpic: (epicId: string) => void;
  onStoryDragStart: (storyId: string, fromEpicId: string) => void;
  onStoryDrop: (storyId: string, fromEpicId: string, toEpicId: string) => void;
  draggingStoryId: string | null;
}) {
  const { orgId: activeOrgId } = useParams();
  const isExpanded = expandedOrgs.has(org.id);
  
  // Fetch projects when expanded
  const { data: projectsData } = useQuery({
    queryKey: ['tree-projects', org.id],
    queryFn: () => api.get<{ data: Project[] }>(`/projects?orgId=${org.id}`),
    enabled: isExpanded,
  });

  const projects = projectsData?.data || [];

  return (
    <TreeNode
      icon={Building2}
      label={org.name}
      href={`/app/organizations/${org.id}`}
      isActive={activeOrgId === org.id}
      isExpanded={isExpanded}
      onToggle={() => toggleOrg(org.id)}
      level={level}
      count={projects.length}
    >
      {projects.map(project => (
        <ProjectNode
          key={project.id}
          project={project}
          orgId={org.id}
          level={level + 1}
          expandedProjects={expandedProjects}
          expandedEpics={expandedEpics}
          toggleProject={toggleProject}
          toggleEpic={toggleEpic}
          onStoryDragStart={onStoryDragStart}
          onStoryDrop={onStoryDrop}
          draggingStoryId={draggingStoryId}
        />
      ))}
    </TreeNode>
  );
}

// Main TreeSidebar Component
export function TreeSidebar() {
  const queryClient = useQueryClient();
  const { orgId, projectId, epicId } = useParams();
  
  // Expanded states
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(new Set());
  
  // Drag state
  const [draggingStoryId, setDraggingStoryId] = useState<string | null>(null);
  
  // Fetch organizations
  const { data: orgsData, isLoading } = useQuery({
    queryKey: ['tree-orgs'],
    queryFn: () => api.get<{ data: Organization[] }>('/orgs'),
  });
  
  // Auto-expand based on current route
  useEffect(() => {
    if (orgId) {
      setExpandedOrgs(prev => new Set([...prev, orgId]));
    }
    if (projectId) {
      setExpandedProjects(prev => new Set([...prev, projectId]));
    }
    if (epicId) {
      setExpandedEpics(prev => new Set([...prev, epicId]));
    }
  }, [orgId, projectId, epicId]);

  // Toggle functions
  const toggleOrg = (id: string) => {
    setExpandedOrgs(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleProject = (id: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleEpic = (id: string) => {
    setExpandedEpics(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  
  // Move story mutation (update KB document epic_id)
  const moveStoryMutation = useMutation({
    mutationFn: async ({ storyId, toEpicId }: { storyId: string; toEpicId: string }) => {
      return api.patch(`/kb/documents/${storyId}`, { epic_id: toEpicId, change_reason: 'Moved between epics via drag-and-drop' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tree-stories'] });
      queryClient.invalidateQueries({ queryKey: ['story'] });
    },
  });
  
  // Drag handlers
  const handleStoryDragStart = (storyId: string, _fromEpicId: string) => {
    setDraggingStoryId(storyId);
  };
  
  const handleStoryDrop = (storyId: string, fromEpicId: string, toEpicId: string) => {
    setDraggingStoryId(null);
    
    if (fromEpicId !== toEpicId) {
      moveStoryMutation.mutate({ storyId, toEpicId });
    }
  };

  const orgs = orgsData?.data || [];

  return (
    <div className="flex-1 overflow-y-auto py-2 px-2">
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-neutral-400 dark:text-neutral-500" />
        </div>
      ) : orgs.length === 0 ? (
        <div className="text-center py-8 px-4">
          <Building2 className="w-8 h-8 mx-auto text-neutral-300 dark:text-neutral-600 mb-2" />
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No organizations</p>
        </div>
      ) : (
        <div className="space-y-0.5">
          {orgs.map(org => (
            <OrgNode
              key={org.id}
              org={org}
              level={0}
              expandedOrgs={expandedOrgs}
              expandedProjects={expandedProjects}
              expandedEpics={expandedEpics}
              toggleOrg={toggleOrg}
              toggleProject={toggleProject}
              toggleEpic={toggleEpic}
              onStoryDragStart={handleStoryDragStart}
              onStoryDrop={handleStoryDrop}
              draggingStoryId={draggingStoryId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
